import { useEffect, useMemo, useState } from 'react';
import { Loader2, Paperclip } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { apiClient } from '@features/shared/api';
import type { QuotationAttachment } from '../../types/quotation.types';
import { guessPreviewKind } from '../../utils/previewKind';
import { ReviewImageViewer } from './ReviewImageViewer';
import { ReviewPdfViewer } from './ReviewPdfViewer';
import { ReviewSpreadsheetGridViewer } from './ReviewSpreadsheetGridViewer';
import { ReviewUnavailableState } from './ReviewUnavailableState';

// Source panel for manual-upload drafts (CSC-283 Layer 3 multi-file flow). Mirrors
// EmailReviewSourcePanel's attachments tab UI but without the extraction-selection
// machinery — manual drafts don't have a retry-extraction endpoint, the panel is a
// read-only preview of the files sales uploaded.
//
// Lazy-fetches bytes via `/api/quotations/{quotationId}/attachment/{id}` and reuses the
// existing review viewers (PDF, image, spreadsheet) so the look matches the email
// branch.

function useAttachmentBlob(quotationId: number, attachment: QuotationAttachment | null) {
  const [blob, setBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!attachment) {
      setBlob(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setBlob(null);
    void apiClient
      .get<Blob>(`/quotations/${quotationId}/attachment/${attachment.id}`, { responseType: 'blob' })
      .then((response) => {
        if (cancelled) return;
        setBlob(response.data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load attachment');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [quotationId, attachment]);

  return { blob, error, loading };
}

interface AttachmentViewerProps {
  quotationId: number;
  attachment: QuotationAttachment;
}

function AttachmentViewer({ quotationId, attachment }: AttachmentViewerProps) {
  const { blob, error, loading } = useAttachmentBlob(quotationId, attachment);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const file = useMemo(
    () => (blob ? new File([blob], attachment.originalFileName, { type: attachment.mimeType }) : null),
    [blob, attachment.originalFileName, attachment.mimeType],
  );

  useEffect(() => {
    if (!blob) {
      setObjectUrl(null);
      return;
    }
    const url = URL.createObjectURL(blob);
    setObjectUrl(url);
    return () => {
      URL.revokeObjectURL(url);
      setObjectUrl((current) => (current === url ? null : current));
    };
  }, [blob]);

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading {attachment.originalFileName}…
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex h-full items-center justify-center px-4 text-center text-sm text-destructive">
        {error}
      </div>
    );
  }
  if (!objectUrl || !file) {
    return <ReviewUnavailableState fileName={attachment.originalFileName} />;
  }

  const kind = guessPreviewKind(attachment.originalFileName);
  if (kind === 'image') return <ReviewImageViewer src={objectUrl} alt={attachment.originalFileName} />;
  if (kind === 'pdf') return <ReviewPdfViewer src={objectUrl} fileName={attachment.originalFileName} />;
  if (kind === 'spreadsheet') {
    return <ReviewSpreadsheetGridViewer file={file} fileName={attachment.originalFileName} />;
  }
  return <ReviewUnavailableState fileName={attachment.originalFileName} />;
}

interface ManualUploadReviewSourcePanelProps {
  quotationId: number;
  attachments: QuotationAttachment[];
}

export function ManualUploadReviewSourcePanel({
  quotationId,
  attachments,
}: ManualUploadReviewSourcePanelProps) {
  const [activeId, setActiveId] = useState<number | null>(
    attachments.length > 0 ? attachments[0].id : null,
  );

  // Re-seed the active pointer when the attachment set changes (e.g. user navigates
  // between drafts in review mode). Falls back to the first file if the previously
  // active id is gone.
  useEffect(() => {
    if (attachments.length === 0) {
      setActiveId(null);
      return;
    }
    if (activeId == null || !attachments.some((a) => a.id === activeId)) {
      setActiveId(attachments[0].id);
    }
  }, [attachments, activeId]);

  const activeAttachment = attachments.find((a) => a.id === activeId) ?? null;

  if (attachments.length === 0) {
    return <ReviewUnavailableState fileName={null} />;
  }

  return (
    <div className="flex h-full w-full flex-col">
      <div className="flex flex-col gap-1.5 border-b px-3 py-2">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Paperclip className="h-3 w-3" />
          <span>
            <strong>{attachments.length}</strong> attachment{attachments.length === 1 ? '' : 's'}
          </span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {attachments.map((att) => {
            const isActive = att.id === activeId;
            return (
              <Button
                key={att.id}
                type="button"
                variant={isActive ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveId(att.id)}
                className="h-7 gap-1 text-xs"
              >
                <span>{att.originalFileName}</span>
              </Button>
            );
          })}
        </div>
      </div>
      <div className="flex-1 overflow-hidden">
        {activeAttachment && (
          <AttachmentViewer quotationId={quotationId} attachment={activeAttachment} />
        )}
      </div>
    </div>
  );
}
